/* Members — everyone in the church, and what they may do.
 *
 * REPLACES THE MODERATORS TAB, which could not work: it filtered the church
 * member list on is_moderator, a flag that only exists on GROUP rows, so the
 * church row's copy is always false. The list could therefore only ever show
 * the Church Admin, and "Make moderator" appeared to do nothing at all.
 *
 * The deeper problem was two screens both answering "who has authority?".
 * Permissions now defines what roles MEAN; this is where you see people and
 * give them one. Define in one place, assign in another.
 *
 * THE CHURCH ADMIN IS SHOWN BUT NOT EDITABLE. Being admin is users.role,
 * checked before any role is read, so it already carries every permission —
 * offering to change it could only mislead, which is exactly what the old
 * Role dropdown did when it displayed "Participant" beside the person still
 * running the church.
 *
 * EVERYTHING WE ASKED THEM, AND A WAY TO NARROW IT. Gender, age and every
 * answer to the church's own signup questions are columns; the same rule
 * builder a meeting or a message uses narrows the table. So "women aged
 * 20–30 who are baptised" is a list you can look at here before you invite
 * them anywhere — and it is the same list, because the server answers both
 * from one definition.
 *
 * PAGED ON THE SERVER. The first version fetched one page of twenty and
 * searched that in the browser, so a church of twenty-seven said "20 in this
 * church" and could not find the other seven.
 */
import { useEffect, useMemo, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import {
  Check,
  Columns3,
  ExternalLink,
  Filter,
  Loader2,
  Search,
  Shield,
  Users,
  UsersRound,
  X,
} from 'lucide-react'
import { Modal } from '@/components/ui/modal'
import { Household } from '@/features/account/FamilyPage'
import { familyNote } from '@/features/attendance/parts'
import { Card } from '@/components/ui/card'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { SelectField } from '@/components/ui/field'
import {
  useMe,
  useMemberTable,
  useMyPermissions,
  useRoles,
  useSetModerator,
} from '@/api/queries'
import { cn } from '@/lib/utils'
import { useDebounced } from '@/lib/useDebounced'
import {
  EMPTY_FILTER,
  RuleBuilder,
  completeFilter,
} from './audience/RuleBuilder'
import type { AudienceField, AudienceFilter, MemberRow as Row } from '@/types/api'

const PAGE_SIZE = 25

/* Which columns show. Remembered per browser — a convenience, so it is
   allowed to forget. */
const COLUMNS_KEY = 'cn.members.columns'

function loadHidden(): Set<string> | null {
  try {
    const raw = window.localStorage.getItem(COLUMNS_KEY)
    return raw ? new Set(JSON.parse(raw) as string[]) : null
  } catch {
    return null
  }
}

function saveHidden(hidden: Set<string>) {
  try {
    window.localStorage.setItem(COLUMNS_KEY, JSON.stringify([...hidden]))
  } catch {
    /* private window or blocked storage — the choice just is not kept */
  }
}

export function MembersPage() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<AudienceFilter>(EMPTY_FILTER)
  const [filterOpen, setFilterOpen] = useState(false)
  const [page, setPage] = useState(1)

  const q = useDebounced(query.trim())
  const applied = useDebounced(JSON.stringify(completeFilter(filter)), 400)
  const appliedFilter = useMemo(
    () => JSON.parse(applied) as AudienceFilter | null,
    [applied],
  )

  // A new search or new rules start from the first page.
  useEffect(() => setPage(1), [q, applied])

  const table = useMemberTable(churchId, {
    q: q || undefined,
    audience_filter: appliedFilter,
    page,
    page_size: PAGE_SIZE,
  })
  const roles = useRoles(churchId)
  const permissions = useMyPermissions(churchId)
  const [familyOf, setFamilyOf] = useState<Row | null>(null)

  const fields = table.data?.fields ?? []
  const [hidden, setHidden] = useState<Set<string> | null>(loadHidden)
  /* By default every profile column and the first three signup questions;
     a church with fifteen questions should not open as a wall of columns. */
  const hiddenNow = useMemo(() => {
    if (hidden) return hidden
    const custom = fields.filter((f) => f.type !== 'gender' && f.type !== 'age')
    return new Set(custom.slice(3).map((f) => f.key))
  }, [hidden, fields])

  function toggleColumn(key: string) {
    const next = new Set(hiddenNow)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setHidden(next)
    saveHidden(next)
  }

  const isAdmin = me?.role === 'church_admin' || me?.role === 'super_admin'
  /* Adding a child to somebody's household is the same authority as
     inviting them, or keeping attendance. */
  const canAddFamily =
    isAdmin ||
    (permissions.data ?? []).some(
      (p) => p === 'members.invite' || p === 'attendance.manage',
    )
  const ruleCount = appliedFilter?.rules.length ?? 0
  const narrowed = Boolean(q) || ruleCount > 0

  if (!me) return null

  if (!churchId) {
    return (
      <Card className="p-s8 text-center">
        <Users className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
        <p className="mt-s3 text-md font-bold">No church yet</p>
        <p className="mt-1 text-base text-ink-2">
          Join a church to see its members.
        </p>
      </Card>
    )
  }

  const data = table.data
  const total = data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const shownFields = fields.filter((f) => !hiddenNow.has(f.key))

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Members</h1>
        <p className="mt-0.5 text-base text-ink-2">
          {data === undefined
            ? '…'
            : narrowed
              ? `${total} ${total === 1 ? 'person matches' : 'people match'}`
              : `${total} in this church`}
        </p>
      </header>

      <Card className="space-y-s3 p-s3">
        <div className="flex flex-wrap items-center gap-s2">
          <div className="relative min-w-[200px] flex-1">
            <Search
              className="pointer-events-none absolute left-s3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2"
              aria-hidden
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, mobile or email"
              aria-label="Search members"
              className="w-full rounded-pill border border-line-2 bg-surface-2 py-s2 pl-[38px] pr-s3 text-base outline-none focus:shadow-focus"
            />
          </div>
          <Button
            size="sm"
            variant={filterOpen || ruleCount ? 'outline' : 'secondary'}
            onClick={() => setFilterOpen((v) => !v)}
            aria-expanded={filterOpen}
            className={cn(ruleCount ? 'border-brand text-brand' : '')}
          >
            <Filter className="h-4 w-4" aria-hidden />
            Filter{ruleCount ? ` · ${ruleCount}` : ''}
          </Button>
          <ColumnsMenu fields={fields} hidden={hiddenNow} onToggle={toggleColumn} />
        </div>

        {filterOpen ? (
          <div className="space-y-s3 border-t border-line-2 pt-s3">
            <RuleBuilder churchId={churchId} value={filter} onChange={setFilter} />
            {filter.rules.length ? (
              <div className="flex flex-wrap items-center justify-between gap-s2">
                {data?.label ? (
                  <p className="text-sm text-ink-2">
                    Showing: <span className="font-semibold text-ink">{data.label}</span>
                  </p>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={() => setFilter(EMPTY_FILTER)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-ink-2 hover:text-ink"
                >
                  <X className="h-3.5 w-3.5" aria-hidden /> Clear rules
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      {table.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : null}

      {/* The endpoint is admin-only, so a member gets a 403 rather than an
          empty list. A rule the server refuses lands here too, and its
          sentence says which. */}
      {table.isError ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          {(table.error as Error).message ||
            'The church member list is for church administrators.'}
        </Card>
      ) : null}

      {data && !data.items.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          {narrowed ? 'Nobody matches.' : 'No members yet.'}
        </Card>
      ) : null}

      {data && data.items.length ? (
        <Card className="overflow-hidden p-0">
          <div className={cn('overflow-x-auto', table.isFetching && 'opacity-70')}>
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-line-2 text-sm text-ink-2">
                  <Th className="sticky left-0 z-[1] min-w-[220px] bg-surface">NAME</Th>
                  <Th className="min-w-[140px]">MOBILE</Th>
                  {shownFields.map((f) => (
                    <Th key={f.key} className="min-w-[110px]">
                      {f.label.toUpperCase()}
                    </Th>
                  ))}
                  <Th className="min-w-[180px]">GROUPS</Th>
                  <Th className="min-w-[190px]">CHURCH ROLE</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((m) => (
                  <MemberRow
                    key={m.id}
                    member={m}
                    fields={shownFields}
                    churchId={churchId}
                    roles={roles.data ?? []}
                    canEdit={isAdmin && m.id !== me.id && !m.household_of}
                    onFamily={canAddFamily && !m.household_of ? () => setFamilyOf(m) : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {total > PAGE_SIZE ? (
        <div className="flex flex-wrap items-center justify-between gap-s3">
          <span className="text-base text-ink-2">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-s2">
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span className="text-sm text-ink-2">
              Page {page} of {lastPage}
            </span>
            <Button
              size="sm"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {familyOf ? (
        <Modal
          open
          onOpenChange={(o) => !o && setFamilyOf(null)}
          title={`${familyOf.name.split(' ')[0]}'s family`}
          width="520px"
        >
          <div className="p-s4">
            <Household churchId={churchId} headId={familyOf.id} headName={familyOf.name} />
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn('whitespace-nowrap px-s4 py-s3 font-semibold', className)}>
      {children}
    </th>
  )
}

function ColumnsMenu({
  fields,
  hidden,
  onToggle,
}: {
  fields: AudienceField[]
  hidden: Set<string>
  onToggle: (key: string) => void
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button size="sm" variant="secondary" disabled={!fields.length}>
          <Columns3 className="h-4 w-4" aria-hidden />
          Columns
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-50 max-h-[60vh] w-64 overflow-y-auto rounded-lg border border-line-2 bg-surface p-s2 shadow-3"
        >
          <p className="px-s2 pb-s2 pt-1 text-xs font-semibold uppercase tracking-wide text-ink-2">
            Show these columns
          </p>
          {fields.map((f) => {
            const on = !hidden.has(f.key)
            return (
              <button
                key={f.key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={on}
                onClick={() => onToggle(f.key)}
                className="flex w-full items-center gap-s2 rounded px-s2 py-1.5 text-left text-base hover:bg-hover"
              >
                <span
                  className={cn(
                    'grid h-4 w-4 shrink-0 place-items-center rounded-sm border',
                    on ? 'border-brand bg-brand text-ink-on-brand' : 'border-line',
                  )}
                >
                  {on ? <Check className="h-3 w-3" /> : null}
                </span>
                <span className="truncate">{f.label}</span>
              </button>
            )
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

/** One answer, as a person would read it. */
function Answer({ field, member }: { field: AudienceField; member: Row }) {
  const muted = <span className="text-ink-3">—</span>
  if (field.type === 'gender') {
    return member.gender ? <>{member.gender === 'female' ? 'Female' : 'Male'}</> : muted
  }
  if (field.type === 'age') {
    return member.age !== null ? <>{member.age}</> : muted
  }
  const v = member.answers[field.key]
  /* An unticked box is saved as a blank answer, so for a check box "no
     answer" means No — a dash there would read as "never asked". */
  if (field.type === 'check_box') return <>{v === 'true' ? 'Yes' : 'No'}</>
  if (v === undefined) return muted
  switch (field.type) {
    case 'date_time': {
      const d = new Date(v)
      return Number.isNaN(d.getTime()) ? (
        <>{v}</>
      ) : (
        <>{d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</>
      )
    }
    case 'image':
      return /^https?:\/\//.test(v) ? (
        <a
          href={v}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-semibold text-brand hover:underline"
        >
          View <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      ) : (
        <>Provided</>
      )
    default:
      return <>{v}</>
  }
}

function MemberRow({
  member,
  fields,
  churchId,
  roles,
  canEdit,
  onFamily,
}: {
  member: Row
  fields: AudienceField[]
  churchId: string
  roles: { id: string; name: string }[]
  canEdit: boolean
  /** Opens this person's household, to add a family member. */
  onFamily?: () => void
}) {
  const setModerator = useSetModerator(churchId)
  const isAdmin = member.role === 'church_admin' || member.role === 'super_admin'

  return (
    <tr className="border-b border-line-2 last:border-0">
      <td className="sticky left-0 z-[1] bg-surface px-s4 py-s3">
        <div className="flex items-center gap-s3">
          <Avatar name={member.name} src={member.avatar_url} size="sm" />
          <span className="min-w-0">
            <span className="block truncate font-semibold">{member.name}</span>
            {member.household_of ? (
              <span className="block max-w-[200px] truncate text-sm text-ink-2">
                {familyNote(member.household_of, member.relation)}
              </span>
            ) : member.email ? (
              <span className="block max-w-[200px] truncate text-sm text-ink-2">
                {member.email}
              </span>
            ) : null}
          </span>
          {onFamily ? (
            <button
              type="button"
              onClick={onFamily}
              aria-label={`${member.name}'s family`}
              title="Family"
              className="ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 hover:bg-hover-2"
            >
              <UsersRound className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </div>
      </td>

      <td className="whitespace-nowrap px-s4 py-s3 text-base text-ink-2">
        {member.household_of ? '—' : member.mobile ?? '—'}
      </td>

      {fields.map((f) => (
        <td key={f.key} className="max-w-[200px] truncate px-s4 py-s3 text-base">
          <Answer field={f} member={member} />
        </td>
      ))}

      <td className="px-s4 py-s3">
        {/* The count first — "how many groups is this person in?" is the
            question being asked — with the names underneath for the answer. */}
        <span className="block text-base">
          {member.groups.length}
          {member.groups.length === 1 ? ' group' : ' groups'}
        </span>
        {member.groups.length ? (
          <span className="block max-w-[220px] truncate text-sm text-ink-2">
            {member.groups.join(' · ')}
          </span>
        ) : null}
      </td>

      <td className="px-s4 py-s3">
        {isAdmin ? (
          /* Not a dropdown. Being admin already carries every permission, so
             a control offering to change it would be lying. */
          <span className="inline-flex items-center gap-s2 whitespace-nowrap rounded-pill bg-warning-soft px-s3 py-0.5 text-sm font-semibold text-warning-ink">
            <Shield className="h-3.5 w-3.5" aria-hidden />
            Church Admin
          </span>
        ) : canEdit ? (
          <SelectField
            label="Role"
            size="sm"
            value={member.role_id ?? ''}
            onChange={(e) =>
              setModerator.mutate({
                userId: member.id,
                roleId: e.target.value || null,
                // The flag follows the role rather than leading it — the
                // server derives what somebody may do from the role itself.
                isModerator: Boolean(e.target.value),
              })
            }
            options={[
              { value: '', label: 'No church role' },
              ...roles.map((r) => ({ value: r.id, label: r.name })),
            ]}
          />
        ) : (
          <span className="text-base text-ink-2">
            {member.role_name ?? 'No church role'}
          </span>
        )}
      </td>
    </tr>
  )
}
